import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';

import Title from 'components/summary/Title';
import LegalInfo from 'components/summary/LegalInfo';
import OrderSummary from 'components/orders/OrderSummary';
import { constants as orderConstants } from 'utils/order';
import styled from 'styled-components';

import SummaryPrice from 'components/summary/SummaryPrice';
import PriceButton from 'components/Price/Button';

import TimeErrorModal from 'components/summary/TimeErrorModal';
import ErrorMessage from 'components/ErrorMessage';
import RecurringErrorMessage from 'components/RecurringErrorMessage';
import RecurringHeader from 'components/Recurring/Header';
import GDPROptions from 'components/summary/GDPROptions';

import DiffPriceWhenEdit from 'components/Price/DiffPriceWhenEdit';

import { constants as aditionalOptionsConstants } from 'utils/aditionalOptions';
import { useFeasibility } from 'hooks/useFeasibility';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  actions as orderActions,
  selectors as orderSelectors,
} from 'state/order';
import { selectors as settingsSelectors } from 'state/settings';
import { selectors as authSelectors } from 'state/auth';
import AditionalOption from './AditionalOption';
import useIsSpainCountry from 'hooks/useIsSpainCountry';

const PStyled = styled.p`
  text-align: justify;
  span {
    font-weight: bold;
  }
  a {
    text-decoration: none;
    color: var(--primary500);
    :hover {
      text-decoration: underline;
    }
  }
`;

const Summary = ({
  className,
  isSubmitting,
  hasErrors,
  hasRecurringError,
  hasPastError,
  hasQuoteError,
  shipmentKey,
  isRecurring,
  hasAditionalOptionsError,
  handleDiscard,
  orderType,
}) => {
  const { t } = useTranslation('Summary');
  const dispatch = useDispatch();
  const isEditing = useSelector(orderSelectors.getIsEditing);
  const [acceptedTerms, setAcceptedTerms] = useState(
    useSelector(authSelectors.getUserAcceptedTerms)
  );
  const [commercialCommunications, setCommercialCommunications] = useState(
    useSelector(authSelectors.getCommercialCommunicationsAccepted)
  );

  const { showFeasibilityMessage } = useFeasibility();

  const handleAcceptedTerms = useCallback(
    (value) => {
      setAcceptedTerms(value);
    },
    [setAcceptedTerms]
  );

  const handleCommercialCommunications = useCallback(
    (value) => {
      setCommercialCommunications(value);
    },
    [setCommercialCommunications]
  );

  const handleSubmitOrder = useCallback(() => {
    const submitData = {
      acceptedTerms,
      commercialCommunications,
      needsLegalAuth: !acceptedTerms,
    };
    if (isEditing) {
      dispatch(orderActions.submitEditOrder(submitData));
      return;
    }
    if (showFeasibilityMessage) {
      dispatch(orderActions.submitNonCoreOrder(submitData));
    } else {
      dispatch(orderActions.submitOrder(submitData));
    }
  }, [
    dispatch,
    acceptedTerms,
    commercialCommunications,
    showFeasibilityMessage,
    isEditing,
  ]);

  const confirmButtonText = useMemo(() => {
    // edit text
    if (isEditing) {
      return t('submit_edit_order');
    }
    // regular text
    if (!showFeasibilityMessage) {
      return t('submit_order');
    }
    // rfq text
    return t('submit_non_feasible_order');
  }, [showFeasibilityMessage, isEditing, t]);

  const hasInsurance = orderType === orderConstants.DIRECT_ORDER;
  const isMultiroute =
    [orderConstants.COLLECTION, orderConstants.DISTRIBUTION].indexOf(
      orderType
    ) >= 0;
  const isSpainCountry = useIsSpainCountry();
  const settings = useSelector(settingsSelectors.getCountrySettings);
  const standStillPriceHour = (settings?.standstill || [{}])[0]?.cost_per_hour;
  const transportLaw =
    'https://www.ontruck.com/es/blog/real-decreto-nueva-ley-transporte';

  return (
    <div className={className} data-testid="summary">
      <div className="summary-item-mobile">
        <Title t={t} isRecurring={isRecurring} />

        <div
          className={`${isRecurring ? 'recurring-summary-item-container' : ''}`}
        >
          {isRecurring && <RecurringHeader />}
          <div className="summary-item">
            <OrderSummary isRecurring={isRecurring} />
          </div>
        </div>
      </div>

      {shipmentKey && !isMultiroute && !isRecurring && (
        <div className="summary-item">
          <h1>{t('aditional_options_title')}</h1>
          {hasInsurance && !isEditing && (
            <AditionalOption
              key={aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE}
              name={aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE}
              shipmentKey={shipmentKey}
              className="insurance-checkbox"
            />
          )}
        </div>
      )}

      <div className="summary-item-mobile">
        <div className="summary-item" data-testid="summary-item">
          <SummaryPrice title={t('details')} />
        </div>

        <div className="btn-container">
          {(hasErrors || hasQuoteError) && !hasRecurringError && (
            <ErrorMessage t={t} aditionalOptions={hasAditionalOptionsError} />
          )}
          {hasRecurringError && <RecurringErrorMessage t={t} />}
          {!acceptedTerms && (
            <div className="summary-item">
              <GDPROptions
                className="summary-legal-info"
                t={t}
                handleAcceptedTerms={(_, { value }) =>
                  handleAcceptedTerms(value)
                }
                acceptedTerms={acceptedTerms}
                handleCommercialCommunications={(_, { value }) =>
                  handleCommercialCommunications(value)
                }
                commercialCommunications={commercialCommunications}
              />
            </div>
          )}
          <DiffPriceWhenEdit />

          {(hasAditionalOptionsError || !hasErrors) && !hasQuoteError && (
            <PriceButton
              onClick={handleSubmitOrder}
              text={confirmButtonText}
              disabled={
                !acceptedTerms ||
                isSubmitting ||
                hasErrors ||
                hasAditionalOptionsError
              }
              submitting={isSubmitting}
              t={t}
            />
          )}
          {isEditing && (
            <button type="button" className="as-link" onClick={handleDiscard}>
              <p>{t('discard_edit_order')}</p>
            </button>
          )}
        </div>
        {isSpainCountry ? (
          <PStyled>
            <Trans
              t={t}
              i18nKey="cancel_clarification_spain"
              values={{ standstillLabel: standStillPriceHour }}
              components={{
                span: <span />,
                a: (
                  <a target={'_blank'} rel="noreferrer" href={transportLaw}>
                    ''
                  </a>
                ),
              }}
            />
          </PStyled>
        ) : (
          <p> {t('cancel_clarification')} </p>
        )}
        <LegalInfo className="summary-legal-info" t={t} />
      </div>
      {hasPastError && <TimeErrorModal t={t} />}
    </div>
  );
};

Summary.propTypes = {
  className: PropTypes.string,
  isSubmitting: PropTypes.bool,
  hasPastError: PropTypes.bool,
  hasQuoteError: PropTypes.bool,
  hasErrors: PropTypes.bool,
  shipmentKey: PropTypes.string,
  orderType: PropTypes.string,
  isRecurring: PropTypes.bool,
  hasRecurringError: PropTypes.bool,
  hasAditionalOptionsError: PropTypes.bool,
  isEditing: PropTypes.bool,
  handleDiscard: PropTypes.func,
  quote: PropTypes.object,
  shipperInstructions: PropTypes.string,
};

Summary.defaultProps = {
  isSubmitting: false,
  hasPastError: false,
  hasQuoteError: false,
  hasErrors: false,
  orderType: '',
  isRecurring: false,
  hasRecurringError: false,
  hasAditionalOptionsError: false,
  isEditing: false,
  handleDiscard: () => ({}),
};

export default Summary;
